import { useEffect, useId, useRef, useState } from 'react';
import { cx } from './cx';

export interface MermaidProps {
  /** A mermaid definition string, e.g. `flowchart TD; A-->B`. */
  chart: string;
  className?: string;
}

/** Read a foundation token's resolved value (tokens are hex). */
const token = (name: string) =>
  typeof window !== 'undefined'
    ? getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    : '';

/** Map our palette to mermaid base-theme variables. */
const themeVariables = () => ({
  background: 'transparent',
  primaryColor: token('--c-surface'),
  primaryTextColor: token('--c-fg'),
  primaryBorderColor: token('--c-border-strong') || token('--c-border'),
  secondaryColor: token('--c-muted'),
  tertiaryColor: token('--c-muted-strong'),
  lineColor: token('--c-fg-tertiary'),
  textColor: token('--c-fg'),
  fontSize: '14px',
});

/**
 * Render a mermaid diagram, themed to the active Regask palette. The mermaid
 * library is imported dynamically inside the effect so it code-splits out of
 * the main bundle (only loaded when a diagram is shown). Re-renders when the
 * chart changes or the `<html>` palette class flips (light/dark). On a render
 * error it shows the raw definition instead of throwing.
 */
export const Mermaid = ({ chart, className }: MermaidProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const rawId = useId();
  const id = `m${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;
  // Bumps on palette-class change to force a re-themed re-render.
  const [paletteTick, setPaletteTick] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const obs = new MutationObserver(() => setPaletteTick(t => t + 1));
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    (async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'base',
          themeVariables: themeVariables(),
        });
        const { svg } = await mermaid.render(id, chart);
        if (!cancelled && ref.current) ref.current.innerHTML = svg;
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chart, id, paletteTick]);

  if (failed) {
    return (
      <pre className={cx('overflow-x-auto rounded-lg border border-default bg-muted p-3 text-xs text-fg-secondary', className)}>
        {chart}
      </pre>
    );
  }
  return <div ref={ref} className={cx('flex justify-center', className)} aria-hidden />;
};
